import React, { useState, useEffect } from 'react';
import { api, LogEntry as ApiLogEntry } from '../utils/api';

// Local interface with Date timestamp for display
interface LogEntry extends Omit<ApiLogEntry, 'timestamp'> {
  timestamp: Date;
}

export const Logs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');

  // Load real log data from API
  useEffect(() => {
    const loadLogs = async () => {
      try {
        setIsLoading(true);

        // Fetch logs from API with default limit of 100
        const logsData = await api.logs.getLogs({
          limit: 100,
          startTime: Date.now() - (60 * 60 * 1000) // Last hour
        });

        // Transform timestamp from number to Date object
        const transformedLogs: LogEntry[] = logsData.map(log => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));

        setLogs(transformedLogs);
        setFilteredLogs(transformedLogs);
      } catch (error) {
        console.error('Failed to load logs:', error);
        // Show empty state on error - never use fallback data
        setLogs([]);
        setFilteredLogs([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadLogs();
  }, []);

  // Filter logs based on search and filters
  useEffect(() => {
    let filtered = logs;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === levelFilter);
    }

    // Source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(log => log.source === sourceFilter);
    }

    // Service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(log => log.service === serviceFilter);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, levelFilter, sourceFilter, serviceFilter]);

  const handleRefresh = async () => {
    try {
      setIsLoading(true);

      // Fetch fresh logs from API
      const logsData = await api.logs.getLogs({
        limit: 100,
        startTime: Date.now() - (60 * 60 * 1000) // Last hour
      });

      // Transform timestamp from number to Date object
      const transformedLogs: LogEntry[] = logsData.map(log => ({
        ...log,
        timestamp: new Date(log.timestamp)
      }));

      setLogs(transformedLogs);
      setFilteredLogs(transformedLogs);
    } catch (error) {
      console.error('Failed to refresh logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(filteredLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bws-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'bi-x-circle-fill';
      case 'warn':
        return 'bi-exclamation-triangle-fill';
      case 'info':
        return 'bi-info-circle-fill';
      case 'debug':
        return 'bi-check-circle-fill';
      default:
        return 'bi-info-circle-fill';
    }
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'text-danger bg-danger-subtle border-danger';
      case 'warn':
        return 'text-warning bg-warning-subtle border-warning';
      case 'info':
        return 'text-primary bg-primary-subtle border-primary';
      case 'debug':
        return 'text-secondary bg-secondary-subtle border-secondary';
      default:
        return 'text-secondary bg-secondary-subtle border-secondary';
    }
  };

  const uniqueServices = Array.from(new Set(logs.map(log => log.service)));

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h2 mb-2">System Logs</h1>
          <p className="text-muted">
            Real-time logs from on-chain and off-chain processes
          </p>
        </div>

        <div className="d-flex gap-2">
          <button
            onClick={handleExport}
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
          >
            <i className="bi bi-download"></i>
            Export
          </button>

          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn btn-primary d-flex align-items-center gap-2"
          >
            <i className={`bi bi-arrow-clockwise ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Search */}
            <div className="col-12 col-md-3">
              <div className="position-relative">
                <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control ps-5"
                />
              </div>
            </div>

            {/* Level Filter */}
            <div className="col-12 col-md-3">
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">All Levels</option>
                <option value="error">Error</option>
                <option value="warn">Warning</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </select>
            </div>

            {/* Source Filter */}
            <div className="col-12 col-md-3">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">All Sources</option>
                <option value="on-chain">On-Chain</option>
                <option value="off-chain">Off-Chain</option>
              </select>
            </div>

            {/* Service Filter */}
            <div className="col-12 col-md-3">
              <select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">All Services</option>
                {uniqueServices.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Log Entries */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h5 fw-semibold mb-0">Log Entries</h2>
            <p className="small text-muted mb-0">
              Showing {filteredLogs.length} of {logs.length} entries
            </p>
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-file-earmark-text fs-1 d-block mb-2"></i>
                <p className="mb-0">No logs found matching your filters</p>
              </div>
            ) : (
              filteredLogs.map((log) => {
                const levelIcon = getLevelIcon(log.level);

                return (
                  <div
                    key={log.id}
                    className={`border rounded p-3 ${getLevelColor(log.level)}`}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <i className={`bi ${levelIcon} fs-5 flex-shrink-0`}></i>

                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-2 small">
                          <span className="badge text-uppercase">
                            {log.level}
                          </span>
                          <span className="text-muted">•</span>
                          <span className="fw-medium">
                            {log.service}
                          </span>
                          <span className="text-muted">•</span>
                          <span className="text-muted">
                            {log.source}
                          </span>
                          <span className="text-muted ms-auto">
                            {log.timestamp.toLocaleTimeString()}
                          </span>
                        </div>

                        <p className="small fw-medium mb-2">
                          {log.message}
                        </p>

                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="bg-body-secondary bg-opacity-75 rounded p-2">
                            <details className="small">
                              <summary className="cursor-pointer fw-medium">
                                View metadata
                              </summary>
                              <pre className="mt-2 mb-0 text-body-secondary" style={{ fontSize: '0.75rem' }}>
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {filteredLogs.length > 0 && (
            <div className="mt-4 text-center">
              <button className="btn btn-outline-secondary">
                Load More Entries
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};